import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { UserService } from 'src/app/core/services/user.service';

import { INewProject, IProject } from '../../model';
import { CreateNewProjectDialogComponent } from '../../shared/dialogs/create-new-project-dialog/create-new-project-dialog.component';
import { ProjectActions } from '../../store/actions';
import { ProjectsFacade } from '../../store/facade';

@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss']
})
export class ProjectsComponent implements OnInit, OnDestroy {
  @Input() category: number;

  private _currentPage = 1;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly dialog: MatDialog,
    private readonly projectsFacade: ProjectsFacade,
    private readonly userService: UserService,
    private readonly actions$: Actions
  ) {
    this.actions$
      .pipe(
        ofType(ProjectActions.ActionTypes.DELETE_PROJECT_SUCCESS),
        tap((_) =>
          this.projectsFacade.list({
            page: this._currentPage,
            categoryId: this.category
          })
        ),
        takeUntil(this._destroy$)
      )
      .subscribe((_) => _);
  }

  ngOnInit() {
    this.projectsFacade.clean();
    this.projectsFacade.list({ page: 1, categoryId: this.category });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get isAdmin$() {
    return this.userService.isAdmin$;
  }

  get amountOfPages$() {
    return this.projectsFacade.totalAmountOfProjects$.pipe(
      map((_) => Math.ceil(_ / this.projectsFacade.amountOfProjectsPerPage))
    );
  }

  get currentPage() {
    return this._currentPage;
  }

  get projects$() {
    return this.projectsFacade.projects$;
  }

  get isLoadingProjects$() {
    return this.projectsFacade.isLoading$;
  }

  onSetPage(page: number) {
    this._currentPage = page;

    this.projectsFacade.list({ page, categoryId: this.category });
  }

  onCreateNewProject() {
    const dialogRef = this.dialog.open<
      CreateNewProjectDialogComponent,
      { categoryId: number },
      INewProject
    >(CreateNewProjectDialogComponent, {
      data: { categoryId: this.category }
    });

    dialogRef
      .afterClosed()
      .pipe(tap((_) => this.createProject(_)))
      .subscribe((_) => _);
  }

  onEditProject(project: IProject) {
    const dialogRef = this.dialog.open<
      CreateNewProjectDialogComponent,
      { project: IProject; categoryId: number },
      IProject
    >(CreateNewProjectDialogComponent, {
      data: { project, categoryId: this.category }
    });

    dialogRef
      .afterClosed()
      .pipe(tap((_) => this.editProject(_)))
      .subscribe((_) => _);
  }

  onRemoveProject(id: number) {
    this.projectsFacade.delete(id);
  }

  private createProject(newProject?: INewProject) {
    if (!newProject) {
      return;
    }

    this.projectsFacade.create(newProject);
  }

  private editProject(project?: IProject) {
    if (!project) {
      return;
    }

    this.projectsFacade.edit(project);
  }
}
